import { useState } from 'react';
import { Box } from '@mui/material';
import { CasBaseDialog, CasLoaderWithBackdrop } from 'src/compass/shared/components';
import useRetrieve from 'src/compass/shared/hooks/reactQuery/useRetrieve';
import useUpdateMutation from 'src/compass/shared/hooks/reactQuery/useUpdateMutation';
import ClaimDetails from './ClaimDetails';
import { getColumnColorByReinsuranceYear } from '../ClaimListing/ClaimListing';

interface ClaimDetailsDialogProps {
  onClose: VoidFunction;
  id: number;
  autoCheckout?: boolean;
}

export default function ClaimDetailsDialog({ onClose: paramOnClose, autoCheckout, id }: ClaimDetailsDialogProps) {
  const [sendAutoCheckout, setSendAutoCheckout] = useState(autoCheckout);
  // Only a claim this dialog checked out is checked back in on close - not one that was already
  // checked out to the user, e.g. by hand or by another Claim Details still open beneath this one.
  const [autoCheckedOut, setAutoCheckedOut] = useState(false);

  const queryResult = useRetrieve({
    url: 'Sce/Claims',
    params: { id, autoCheckout: sendAutoCheckout },
    onSuccess: (response: any) => {
      if (sendAutoCheckout) setSendAutoCheckout(false);
      if (response?.autoCheckedOut) setAutoCheckedOut(true);
      return response;
    },
  });

  const { data, isLoading, isRefetching, isError, refetch } = queryResult;

  const { mutate: autoCheckinMutate } = useUpdateMutation({
    url: '/Sce/Claims/Checkout',
    onSuccess: paramOnClose,
    onError: paramOnClose,
  });

  const onClose = () => {
    if (autoCheckedOut)
      autoCheckinMutate({ endorsementId: id, auto: true, action: 'Clear' });
    else paramOnClose();
  };

  if (isLoading) return <CasLoaderWithBackdrop />;

  if (isError) throw new Error('Error retrieving data from the server.');

  const { insuredName, reinsuranceYear, policyNumber, sce } = data;

  const title = <Box>{insuredName}: <span style={getColumnColorByReinsuranceYear(data)}>{reinsuranceYear}</span>-{policyNumber}-{sce}</Box>

  const onUpdated = (closeParentDialogOnSuccess?: boolean) => {
    if (closeParentDialogOnSuccess) onClose();
    else refetch();
  };
  
  return (
    <CasBaseDialog
      open
      maxWidth="xl"
      onClose={onClose}
      title={title}
      showCloseIcon
    > 
      {isRefetching && <CasLoaderWithBackdrop/> }
      <ClaimDetails data={data} onUpdated={onUpdated} autoCheckout={autoCheckout} />
    </CasBaseDialog>
  );
}
